import type { ImportedRecordView } from "@vaulted/shared";
import { SIGN_STATEMENTS, SOURCE_LABELS } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { errorMessage, get, post } from "../api";
import { SourceBadge } from "../components/SourceBadge";
import { Alert, Badge, Button, Loading, Panel, TextAreaField, formatDateTime24 } from "../components/ui";
import { useSession } from "../session";
import { BackLink } from "../components/BackLink";
import { AgreeBox, SignatureBlock, goToNextReview } from "../components/ESignature";

/** A form or chart from the old system, exactly as it was signed there (opening it is logged). */
export function ImportedRecordPage() {
  const { id = "" } = useParams();
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["imported-record", id], queryFn: () => get<ImportedRecordView>(`/api/imported-records/${id}`) });
  const nav = useNavigate();
  const [note, setNote] = useState("");
  const [agree, setAgree] = useState(false);
  const review = useMutation({
    mutationFn: () => post<ImportedRecordView>(`/api/imported-records/${id}/review`, { note, agree }),
    onSuccess: async (r) => {
      qc.setQueryData(["imported-record", id], r);
      await qc.invalidateQueries({ queryKey: ["charts-pending-review"] });
      // On to the next chart waiting for review.
      await goToNextReview(qc, nav, id);
    },
  });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <Alert>{errorMessage(q.error)}</Alert>;
  const r = q.data;
  const where = SOURCE_LABELS[r.source] ?? r.source;
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div className="flex items-center justify-between gap-3 print:hidden">
        <BackLink to={`/clients/${r.clientId}`} label={r.clientName} className="text-sm font-semibold text-brand" />
        <Button variant="quiet" onClick={() => window.print()}>
          Print
        </Button>
      </div>
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="page-title">
          {r.name}
          <span className="hidden print:inline"> · {r.clientName}</span>
        </h1>
        <SourceBadge source={r.source} />
        {r.voided && <Badge tone="off">Voided in {where}</Badge>}
        {r.review.status === "waiting" && <Badge tone="wait">Waiting for MD review</Badge>}
        {r.review.status === "reviewed" && <Badge tone="ok">MD reviewed</Badge>}
      </header>
      <p className="text-sm text-ink-soft">
        Signed in {where}
        {r.signedAt ? ` on ${formatDateTime24(r.signedAt)}` : ""}
        {r.signedBy ? ` by ${r.signedBy}` : ""}. Shown exactly as it was there; it can't be changed.
      </p>
      <Panel className="px-5 py-4">
        <dl className="flex flex-col gap-3">
          {r.content.map((i, n) =>
            i.kind === "heading" ? (
              <h2 key={n} className="mt-2 font-display text-lg">
                {i.label}
              </h2>
            ) : i.kind === "text" ? (
              <p key={n} className="whitespace-pre-line text-sm text-ink-soft">
                {i.label}
              </p>
            ) : i.kind === "answer" ? (
              <div key={n} className="grid gap-1 border-b border-mist pb-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-4">
                <dt className="text-sm font-semibold text-ink-soft">{i.label}</dt>
                <dd className="whitespace-pre-line text-sm">{i.value || <span className="text-muted">—</span>}</dd>
              </div>
            ) : (
              <div key={n} className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-ink-soft">{i.label}</span>
                {i.file ? (
                  i.kind === "signature" || /\.(png|jpe?g|gif|webp)(\?|$)/i.test(i.file) ? (
                    <img src={i.file} alt={i.label} className="max-h-40 w-auto max-w-sm self-start rounded border border-mist bg-white object-contain p-1" />
                  ) : (
                    <a href={i.file} target="_blank" rel="noopener" className="text-sm font-semibold text-brand">
                      Open file
                    </a>
                  )
                ) : i.kind === "signature" && i.value ? (
                  // Signed there, but no picture of the signature came with the export.
                  <span className="text-sm">
                    Signed by {i.value} in {where}
                  </span>
                ) : (
                  <span className="text-sm text-muted">Not signed</span>
                )}
                {i.kind === "signature" && i.value && i.file && <span className="text-xs text-muted">{i.value}</span>}
              </div>
            ),
          )}
        </dl>
      </Panel>
      {r.review.status === "reviewed" && (
        <Panel className="px-5 py-4 text-sm">
          Reviewed by <span className="font-semibold">{r.review.by || "the medical director"}</span>
          {r.review.at ? ` on ${formatDateTime24(r.review.at)}` : ""} {r.review.where === "source" ? `in ${where}` : "in Vaulted"}.
          {r.review.note && <p className="mt-1 whitespace-pre-line text-ink-soft">{r.review.note}</p>}
          {r.reviewSignature && (
            <div className="mt-3">
              <SignatureBlock title="Signed by" sig={r.reviewSignature} />
            </div>
          )}
        </Panel>
      )}
      {r.review.status === "waiting" && can("charts.sign") && (
        <Panel className="flex flex-col gap-3 px-5 py-4 print:hidden">
          <p className="text-sm text-ink-soft">This chart was still waiting for your review in {where}. Your review is recorded here; the chart itself stays as it was signed.</p>
          <TextAreaField label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <AgreeBox statement={SIGN_STATEMENTS.review} checked={agree} onChange={setAgree} />
          {review.error && <Alert>{errorMessage(review.error)}</Alert>}
          <Button variant="primary" className="self-start" busy={review.isPending} disabled={!agree} onClick={() => review.mutate()}>
            Sign review and go to the next
          </Button>
        </Panel>
      )}
    </div>
  );
}
